import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button, Form, FormControl as Input, InputGroup, Spinner } from "react-bootstrap";
import { CircularProgress, FormControlLabel, Radio } from "@material-ui/core";
import { RadioGroup } from "@mui/material";
import { DatePicker, TimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import JoditEditor from "jodit-react";
import { MdCloudUpload, MdPlaylistAdd, MdRefresh, MdWorkHistory } from "react-icons/md";
import moment from "moment";
import Request from "../../Request";
import Attention from "../ReusableComponents/Attention";
import TableEmptyState from "../ReusableComponents/TableEmptyState";
import { usePreferences } from "../../contexts/PreferencesContext";
import { translateJobStatus } from "../../i18n/adminTranslationHelpers";
import { API_URL, STORAGE_URL } from "../../variables";
import ProcessoConfigSummary from "./ProcessoConfigSummary";
import {
  chatOptionKeys,
  proctoringOptionKeys,
  simulationProctoringOptionKeys,
  translateOption,
} from "./processoTranslationOptions";
import "./label.css";

const createInitialConfig = () => ({
  name: "",
  descricao: "",
  regras: "",
  inicio: moment(),
  hora_inicio: moment(),
  fim: moment().add(1, "day"),
  hora_fim: moment().add(1, "hour"),
  restrict_access: false,
  inicio_access: moment().subtract(24, "hours"),
  hora_inicio_access: moment().subtract(24, "hours"),
  fim_access: moment(),
  hora_fim_access: moment(),
  simulado: false,
  online: true,
  fiscalizacao: "all",
  type_time: "all",
  tempo_prova: "",
  full_screen: true,
  screen_sharing: false,
  screen_extended_retrict: true,
  mobile: false,
  required_external_camera: false,
  recording_start_before_enabled: false,
  recording_start_before_minutes: 5,
  face_validation: false,
  show_document: false,
  face_validation_periodicaly: false,
  time_validation_periodicaly: 40,
  chat_type: "all",
  chat_time: 60,
  request_time: 60,
  show_rascunho: true,
  exam_per_time: false,
  need_pause: false,
  has_blocks: false,
  is_interview: false,
  fiscalizacoes: [
    { id: "all", name: "Salvar Vídeo e Imagem - Com streaming em tempo real" },
    { id: "image", name: "Salvar Apenas Imagem - Com streaming em tempo real" },
    { id: "none", name: "Salvar apenas logs - Sem streaming" },
  ],
  fiscalizacoes_simulado: [
    { id: "all", name: "Testar compartilhamento de câmera e áudio" },
    { id: "none", name: "Não compartilhar câmera e áudio" },
  ],
  chat_types: [
    { id: "all", name: "Ambos enviam mensagem" },
    { id: "fiscal_to_student", name: "Apenas o fiscal envia mensagem" },
    { id: "none", name: "Ninguem envia mensagem" },
  ],
});

const ConfigSection = ({ title, children }) => (
  <div className="bulk-process-config-section">
    <h5>{title}</h5>
    {children}
  </div>
);

const ConfigField = ({ label, description, required = true, children }) => (
  <div className="flex fdcolumn flex1 margin-bottom bulk-process-config-field">
    <Form.Label>{label} {required && <Attention />}</Form.Label>
    {description && <small>{description}</small>}
    {children}
  </div>
);

const BooleanRadio = ({ value, onChange, disabled = false, yesLabel, noLabel }) => (
  <RadioGroup value={value} className="bulk-process-radio-row">
    <FormControlLabel
      onClick={() => !disabled && onChange(true)}
      disabled={disabled}
      control={<Radio checked={value == true} value={true} />}
      label={yesLabel}
    />
    <FormControlLabel
      onClick={() => !disabled && onChange(false)}
      disabled={disabled}
      control={<Radio checked={value == false} value={false} />}
      label={noLabel}
    />
  </RadioGroup>
);

const ModalProcessosLote = ({ showModal, setShowModal, faculdadeId, onFinish, changeText }) => {
  const { t } = usePreferences();
  const [activeSection, setActiveSection] = useState("names");
  const [names, setNames] = useState([]);
  const [nameDraft, setNameDraft] = useState("");
  const [editingNameIndex, setEditingNameIndex] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [fileValue, setFileValue] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [faculdade, setFaculdade] = useState(null);
  const [msgErro, setMsgErro] = useState("");

  const [config, setConfig] = useState(createInitialConfig);

  const joditConfig = useMemo(() => ({
    height: 300,
    readonly: loadingImage,
    language: "pt_br",
    toolbarButtonSize: "large",
    buttons: "source,bold,italic,underline,strikethrough,eraser,superscript,subscript,ul,ol,indent,outdent,left,center,right,justify,font,fontsize,paragraph,classSpan,image,brush,,undo,redo,hr,link,symbol,table",
    uploader: {
      url: API_URL + "faculdade/upload_image",
      insertImageAsBase64URI: false,
      imagesExtensions: ["jpg", "png", "jpeg", "gif"],
      headers: { accept: "application/json", Authorization: "Bearer " + localStorage.getItem("token") },
      filesVariableName: () => "imagem",
      withCredentials: false,
      pathVariableName: "path",
      method: "POST",
      prepareData: (formdata) => {
        setLoadingImage(true);
        return formdata;
      },
      isSuccess: () => "Upload realizado com sucesso",
      process: (resp) => {
        setLoadingImage(false);
        return { files: [STORAGE_URL + resp.imagem] };
      },
      error: () => setLoadingImage(false),
      defaultHandlerSuccess: function (resp) {
        if (resp.files && resp.files.length) {
          resp.files.forEach((filename) => {
            this.selection.insertImage(filename);
          });
        }
      },
      defaultHandlerError: () => setLoadingImage(false),
    },
  }), [loadingImage]);

  useEffect(() => {
    if (showModal) {
      getJobs();
      getFaculdade();
      setActiveSection("names");
    }
  }, [showModal]);

  const updateConfig = (key, value) => {
    setConfig((current) => ({ ...current, [key]: value }));
  };

  const mergeConfig = (values) => {
    setConfig((current) => ({ ...current, ...values }));
  };

  const getFaculdade = async () => {
    const response = await Request.Get(`faculdade/get_faculdade/${faculdadeId}`);
    if (response && response.faculdade) {
      setFaculdade(response.faculdade);
      if (response.faculdade?.empresa?.online_proccess === false) {
        mergeConfig({ online: false, regras: "---" });
      }
    }
  };

  const addNamesFromText = (text) => {
    const nextNames = String(text || "")
      .split(/[,\n\r;]/)
      .map((name) => name.trim())
      .filter(Boolean);

    if (nextNames.length === 0) {
      return;
    }

    setNames((current) => {
      const normalized = current.map((name) => name.toLocaleLowerCase());
      const merged = [...current];
      nextNames.forEach((name) => {
        if (!normalized.includes(name.toLocaleLowerCase())) {
          normalized.push(name.toLocaleLowerCase());
          merged.push(name);
        }
      });
      return merged;
    });
  };

  const commitNameDraft = () => {
    addNamesFromText(nameDraft);
    setNameDraft("");
  };

  const hasPendingNames = names.length > 0 || nameDraft.trim().length > 0;

  const goToConfigStep = () => {
    if (!hasPendingNames) {
      return;
    }
    if (nameDraft.trim().length > 0) {
      addNamesFromText(nameDraft);
      setNameDraft("");
    }
    setActiveSection("config");
  };

  const removeName = (index) => {
    setNames((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const startEditName = (index) => {
    setEditingNameIndex(index);
    setEditingNameValue(names[index] || "");
  };

  const commitEditName = () => {
    const nextValue = editingNameValue.trim();
    if (editingNameIndex === null) {
      return;
    }
    setNames((current) => {
      if (nextValue.length === 0) {
        return current.filter((_, itemIndex) => itemIndex !== editingNameIndex);
      }
      const duplicateIndex = current.findIndex((name, itemIndex) => itemIndex !== editingNameIndex && name.toLocaleLowerCase() === nextValue.toLocaleLowerCase());
      if (duplicateIndex !== -1) {
        return current.filter((_, itemIndex) => itemIndex !== editingNameIndex);
      }
      return current.map((name, itemIndex) => itemIndex === editingNameIndex ? nextValue : name);
    });
    setEditingNameIndex(null);
    setEditingNameValue("");
  };

  const getJobs = async (page = 1) => {
    setLoadingJobs(true);
    const response = await Request.Get(`faculdade/get_processos_lote_jobs/${faculdadeId}?page=${page}`);
    if (response === undefined) {
      setLoadingJobs(false);
      return;
    }
    if (response.pagination && response.pagination.current_page == page) {
      setJobs(response.data || []);
      setPagination(response.pagination);
    }
    setLoadingJobs(false);
  };

  const readNamesFile = (file) => {
    setMsgErro("");
    const reader = new FileReader();
    reader.onload = () => {
      const rows = String(reader.result || "")
        .split(/\r?\n/)
        .map((line) => line.split(/[;\t,]/)[0].trim())
        .filter(Boolean);

      addNamesFromText(rows.join(","));
    };
    reader.onerror = () => setMsgErro(t("bulk_vacancy_file_read_error"));
    reader.readAsText(file);
  };

  const buildPayload = () => {
    const canConfigureOnline = faculdade?.empresa?.online_proccess !== false;
    const online = canConfigureOnline ? config.online : false;
    const fiscalizacao = config.simulado ? (config.fiscalizacao == "all" ? "all" : "none") : config.fiscalizacao;
    const typeTime = config.simulado ? "restrict" : config.has_blocks ? "all" : config.type_time;
    const tempoProva = config.simulado ? 4 : config.has_blocks ? "" : config.tempo_prova;
    const requiredExternalCamera = fiscalizacao == "all" && config.mobile == false ? config.required_external_camera : false;
    const recordingStartBefore = config.recording_start_before_enabled == true ? Math.min(30, Math.max(1, parseInt(config.recording_start_before_minutes || 5, 10) || 5)) : 0;

    return {
      names,
      code: "",
      descricao: config.descricao,
      faculdade_id: faculdadeId,
      inicio: moment(config.inicio).format("DD/MM/YYYY"),
      fim: moment(config.fim).format("DD/MM/YYYY"),
      hora_inicio: moment(config.hora_inicio).format("HH:mm"),
      hora_fim: moment(config.hora_fim).format("HH:mm"),
      inicio_access: moment(config.inicio_access).format("DD/MM/YYYY"),
      fim_access: moment(config.fim_access).format("DD/MM/YYYY"),
      hora_inicio_access: moment(config.hora_inicio_access).format("HH:mm"),
      hora_fim_access: moment(config.hora_fim_access).format("HH:mm"),
      restrict_access: config.restrict_access,
      is_interview: config.is_interview,
      regras: canConfigureOnline ? config.regras : "---",
      online,
      need_pause: config.simulado ? false : config.need_pause,
      has_blocks: config.simulado ? false : config.has_blocks,
      fiscalizacao,
      chat_type: config.chat_type,
      show_rascunho: config.show_rascunho,
      simulado: config.simulado,
      type_time: typeTime,
      tempo_prova: tempoProva,
      chat_time: config.chat_time * 60,
      request_time: config.request_time * 60,
      time_validation_periodicaly: config.time_validation_periodicaly,
      recording_start_before_minutes: config.simulado ? 0 : recordingStartBefore,
      full_screen: config.full_screen,
      screen_extended_retrict: config.screen_sharing == true ? true : config.screen_extended_retrict,
      screen_sharing: config.screen_sharing,
      required_external_camera: requiredExternalCamera,
      mobile: config.mobile,
      exam_per_time: config.exam_per_time,
      face_validation: config.face_validation,
      show_document: config.show_document,
      face_validation_periodicaly: config.face_validation_periodicaly,
    };
  };

  const saveBatch = async () => {
    setMsgErro("");
    if (names.length === 0) {
      setMsgErro(t("bulk_vacancy_names_required"));
      setActiveSection("names");
      return;
    }
    if (!config.descricao.trim() || (faculdade?.empresa?.online_proccess !== false && !config.regras.trim())) {
      setMsgErro(t("bulk_vacancy_config_required"));
      setActiveSection("config");
      return;
    }

    setLoading(true);
    const response = await Request.Post("faculdade/store_processos_lote", JSON.stringify(buildPayload()), {
      "Content-Type": "application/json",
    });
    setLoading(false);

    if (response === undefined) {
      setMsgErro(t("error_server_connection"));
      return;
    }
    if (response.errors || response.error) {
      setMsgErro(Object.values(response.errors || { error: response.error }).join("\n"));
      return;
    }

    setNames([]);
    setNameDraft("");
    setConfig(createInitialConfig());
    setFileValue("");
    setActiveSection("names");
    getJobs();
    onFinish();
  };

  const closeModal = () => {
    setShowModal(false);
    setMsgErro("");
    setNames([]);
    setNameDraft("");
    setConfig(createInitialConfig());
    setFileValue("");
    onFinish();
  };

  const entity = changeText ? t("vacancy") : t("process");
  const modalTitle = changeText ? t("bulk_vacancy_title") : t("bulk_process_title");
  const namesLabel = changeText ? t("bulk_vacancy_names_label") : t("bulk_process_names_label");

  return (
    <Modal show={showModal} dialogClassName="modal-90w modal-width" onHide={closeModal}>
      <Modal.Header closeButton closeLabel="">
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {msgErro && <div className="badge badge-danger error-message-badge error-message-badge-dismissible w-100 mb-3" role="alert">
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setMsgErro("")}></button>
          <p style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>{msgErro}</p>
        </div>}

        <div className="bulk-vacancy-stepper">
          <button className={activeSection == "names" ? "active" : ""} onClick={() => setActiveSection("names")} type="button">
            <span>1</span> {t("bulk_vacancy_names_section")}
          </button>
          <button className={activeSection == "config" ? "active" : ""} onClick={goToConfigStep} type="button" disabled={!hasPendingNames}>
            <span>2</span> {t("bulk_vacancy_config_section")}
          </button>
        </div>

        {activeSection == "names" && <div className="bulk-vacancy-section">
          <div className="bulk-vacancy-panel">
            <div className="bulk-vacancy-jobs-header">
              <h5><MdWorkHistory /> {t("bulk_vacancy_jobs_section")}</h5>
              <Button variant="outline-primary" size="sm" onClick={() => getJobs(pagination.current_page)}>
                <MdRefresh /> {t("action_refresh")}
              </Button>
            </div>
            <div className="table-modern-card w-100">
              <div className="table-modern-scroll">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("label_module_status")}</th>
                      <th>{t("legacy_erro")}</th>
                      <th>{t("legacy_progresso")}</th>
                      <th>{t("legacy_total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loadingJobs && jobs.length === 0 ?
                      <tr className="table-empty-row">
                        <td colSpan="4">
                          <TableEmptyState title={t("legacy_nenhuma_execucao_encontrada")} icon="📋" />
                        </td>
                      </tr> :
                      !loadingJobs && jobs.map((job) => (
                        <tr key={job.id}>
                          <td><span className={`badge ${job.status == "Finalizado" ? "badge-success" : job.status == "Erro na execução" ? "badge-danger" : "badge-warning"}`}>{translateJobStatus(t, job.status)}</span></td>
                          <td>{job.error_friendly || "-"}</td>
                          <td>{job.progresso || 0}</td>
                          <td>{job.total_progresso || 0}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {loadingJobs && <div className="row m-0"><div className="col-12 d-flex justify-content-center align-items-center"><CircularProgress /></div></div>}
              </div>
            </div>
          </div>

          <div className="bulk-vacancy-panel">
            <h5><MdPlaylistAdd /> {namesLabel}</h5>
          <div className="bulk-vacancy-summary">
            <strong>{names.length}</strong>
            <span>{t("bulk_vacancy_names_count")}</span>
          </div>
          <InputGroup>
            <div className="flex fdcolumn flex1 margin-bottom">
              <Form.Label>{namesLabel} <Attention /></Form.Label>
              <small>{t("bulk_vacancy_names_description")}</small>
              <div className="bulk-vacancy-token-field">
                {names.map((name, index) => (
                  editingNameIndex === index ? (
                    <input
                      key={`edit-${index}`}
                      className="bulk-vacancy-token-edit"
                      value={editingNameValue}
                      autoFocus
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onBlur={commitEditName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          commitEditName();
                        }
                        if (e.key === "Escape") {
                          setEditingNameIndex(null);
                          setEditingNameValue("");
                        }
                      }}
                    />
                  ) : (
                    <span key={`${name}-${index}`} className="bulk-vacancy-token" title={t("bulk_vacancy_name_edit_hint")}>
                      <button type="button" className="bulk-vacancy-token-label" onClick={() => startEditName(index)}>
                        {name}
                      </button>
                      <button type="button" className="bulk-vacancy-token-remove" onClick={() => removeName(index)} aria-label={t("bulk_vacancy_name_remove")}>
                        ×
                      </button>
                    </span>
                  )
                ))}
                <input
                  className="bulk-vacancy-token-input"
                  value={nameDraft}
                  placeholder={t("bulk_vacancy_names_placeholder")}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/[,\n\r;]/.test(value)) {
                      addNamesFromText(value);
                      setNameDraft("");
                      return;
                    }
                    setNameDraft(value);
                  }}
                  onBlur={commitNameDraft}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (/[,\n\r;]/.test(pasted)) {
                      e.preventDefault();
                      addNamesFromText(pasted);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      commitNameDraft();
                    }
                  }}
                />
              </div>
            </div>
          </InputGroup>
          <InputGroup>
            <div className="flex fdcolumn flex1 margin-bottom">
              <Form.Label>{t("bulk_vacancy_file_label")}</Form.Label>
              <small>{t("bulk_vacancy_file_description")}</small>
              <div className="modern-file-upload">
                <input
                  id="bulk-vacancy-names-file"
                  type="file"
                  accept=".csv,.txt"
                  value={fileValue}
                  className="modern-file-upload-input"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setFileValue("");
                    if (file) {
                      readNamesFile(file);
                    }
                  }}
                />
                <label className="modern-file-upload-trigger" htmlFor="bulk-vacancy-names-file">
                  <MdCloudUpload /> {t("bulk_vacancy_file_label")}
                </label>
                <span className="modern-file-upload-empty">{t("bulk_vacancy_file_description")}</span>
              </div>
            </div>
          </InputGroup>
          </div>
        </div>}

        {activeSection == "config" && <div className="bulk-vacancy-section bulk-process-config-layout">
          <Form className="config-form bulk-process-config-form">
            <ConfigSection title={t("summary_identification")}>
              <InputGroup>
                <ConfigField label={t("label_description")}>
                  <Input
                    placeholder={`${t("label_description")} ${changeText ? t("label_of_vacancy_position") : t("legacy_do_processo")}`}
                    value={config.descricao}
                    onChange={(e) => updateConfig("descricao", e.target.value)}
                    required
                    as="textarea"
                  />
                </ConfigField>
              </InputGroup>

              {faculdade?.empresa?.online_proccess !== false && <InputGroup>
                <ConfigField label={`${t("label_rules")} ${changeText ? t("label_of_vacancy_position") : t("legacy_do_processo")}`}>
                  {loadingImage && <div className="spinner-border text-primary spinner-border-sm mb-2" role="status"></div>}
                  <JoditEditor
                    value={config.regras}
                    config={joditConfig}
                    height={400}
                    tabIndex={1}
                    onBlur={(newContent) => updateConfig("regras", newContent)}
                  />
                </ConfigField>
              </InputGroup>}
            </ConfigSection>

            {faculdade?.empresa?.online_proccess !== false && <ConfigSection title={t("summary_modality")}>
              <InputGroup>
                <ConfigField label={t("ui_a_avaliacao_e_online_63f6668d")}>
                  <BooleanRadio
                    value={config.online}
                    yesLabel={t("answer_yes")}
                    noLabel={t("answer_no")}
                    onChange={(online) => mergeConfig({ online })}
                  />
                </ConfigField>
              </InputGroup>

              {config.online == true && <InputGroup>
                <ConfigField label={t("ui_e_um_avaliacao_oral_031e9629")} description={t("ui_se_habilitado_esta_vaga_cargo_sera_uma_entrevista__b2e828a7")}>
                  <BooleanRadio value={config.is_interview} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(is_interview) => updateConfig("is_interview", is_interview)} />
                </ConfigField>
              </InputGroup>}
            </ConfigSection>}

            <ConfigSection title={t("summary_schedule")}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <InputGroup>
                  <ConfigField label={t("legacy_data_de_inicio")}>
                    <DatePicker
                      autoOk
                      margin="normal"
                      style={{ width: "100%" }}
                      invalidDateMessage={t("ui_data_invalida_76fc5825")}
                      ampm={false}
                      cancelLabel={t("legacy_cancelar")}
                      clearLabel={t("action_clear")}
                      format="DD/MM/YYYY"
                      value={config.inicio}
                      onChange={(date) => updateConfig("inicio", date)}
                    />
                  </ConfigField>
                  <ConfigField label={t("ui_horario_de_inicio_701ca30c")}>
                    <TimePicker
                      autoOk
                      margin="normal"
                      style={{ width: "100%" }}
                      invalidDateMessage={t("ui_horario_invalido_6d7a8390")}
                      ampm={false}
                      cancelLabel={t("legacy_cancelar")}
                      clearLabel={t("action_clear")}
                      value={config.hora_inicio}
                      onChange={(date) => updateConfig("hora_inicio", date)}
                    />
                  </ConfigField>
                </InputGroup>
                <InputGroup>
                  <ConfigField label={t("legacy_data_de_encerramento")}>
                    <DatePicker
                      autoOk
                      margin="normal"
                      style={{ width: "100%" }}
                      invalidDateMessage={t("ui_data_invalida_76fc5825")}
                      ampm={false}
                      cancelLabel={t("legacy_cancelar")}
                      clearLabel={t("action_clear")}
                      format="DD/MM/YYYY"
                      value={config.fim}
                      onChange={(date) => updateConfig("fim", date)}
                    />
                  </ConfigField>
                  <ConfigField label={t("label_end_time")}>
                    <TimePicker
                      autoOk
                      margin="normal"
                      style={{ width: "100%" }}
                      invalidDateMessage={t("ui_horario_invalido_6d7a8390")}
                      ampm={false}
                      cancelLabel={t("legacy_cancelar")}
                      clearLabel={t("action_clear")}
                      value={config.hora_fim}
                      onChange={(date) => updateConfig("hora_fim", date)}
                    />
                  </ConfigField>
                </InputGroup>
              </MuiPickersUtilsProvider>
            </ConfigSection>

            {faculdade?.empresa?.online_proccess !== false && config.online == true && <ConfigSection title={t("bulk_vacancy_exam_settings")}>
              <InputGroup>
                <ConfigField label={t("ui_e_um_simulado_para_teste_de_compatibilidade_dos_ca_be36cb46")} description={t("ui_se_habilitado_essa_vaga_cargo_sera_um_teste_para_o_6222b09d")}>
                  <BooleanRadio
                    value={config.simulado}
                    yesLabel={t("answer_yes")}
                    noLabel={t("answer_no")}
                    onChange={(simulado) => {
                      mergeConfig(simulado
                        ? { simulado, type_time: "restrict", tempo_prova: 4, has_blocks: false, fiscalizacao: "all", chat_type: "none", exam_per_time: false, recording_start_before_enabled: false, recording_start_before_minutes: 5 }
                        : { simulado, chat_type: "all", type_time: "all", tempo_prova: "" });
                    }}
                  />
                </ConfigField>
              </InputGroup>

              <InputGroup>
                <ConfigField label={t("ui_cada_avaliacao_tera_um_tempo_maximo_para_fazer_e0de075c")} description={t("ui_se_habilitado_na_hora_de_criar_a_prova_sera_obriga_8f8b7f7f")}>
                  <BooleanRadio value={config.exam_per_time} disabled={config.simulado} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(exam_per_time) => updateConfig("exam_per_time", exam_per_time)} />
                </ConfigField>
              </InputGroup>

              <InputGroup>
                <ConfigField label={t("ui_o_candidato_podera_solicitar_pausa_durante_a_prova_6d5871fc")} description={t("ui_se_habilitado_e_exibilido_um_botao_de_pedir_pausa__a63c10d6")}>
                  <BooleanRadio value={config.need_pause} disabled={config.simulado} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(need_pause) => updateConfig("need_pause", need_pause)} />
                </ConfigField>
              </InputGroup>

              <InputGroup>
                <ConfigField label={t("ui_o_candidato_podera_acessar_por_dispositivos_moveis_f529a234")} description={t("ui_se_habilitado_a_avaliacao_tambem_podera_ser_realiz_ccf181b9")}>
                  <BooleanRadio
                    value={config.mobile}
                    yesLabel={t("answer_yes")}
                    noLabel={t("answer_no")}
                    onChange={(mobile) => mergeConfig(mobile
                      ? { mobile, screen_sharing: false, required_external_camera: false, face_validation: false, show_document: false, face_validation_periodicaly: false }
                      : { mobile })}
                  />
                </ConfigField>
              </InputGroup>

              <InputGroup>
                <ConfigField label={t("ui_disponibilizar_campo_para_rascunho_em_cada_questao_59890895")} description={t("ui_se_habilitado_em_cada_questao_tera_um_botao_de_ras_29b1be64")}>
                  <BooleanRadio value={config.show_rascunho} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(show_rascunho) => updateConfig("show_rascunho", show_rascunho)} />
                </ConfigField>
              </InputGroup>

              <InputGroup>
                <ConfigField label={t("ui_obrigar_o_uso_de_tela_cheia_para_ver_a_avaliacao_81575207")} description={t("ui_se_habilitado_o_candidato_so_conseguira_fazer_a_av_c5c6d2f3")}>
                  <BooleanRadio value={config.full_screen} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(full_screen) => updateConfig("full_screen", full_screen)} />
                </ConfigField>
              </InputGroup>

              <InputGroup>
                <ConfigField label={t("form_exam_time")}>
                  <RadioGroup
                    value={config.type_time}
                    className="bulk-process-radio-row"
                    onChange={(e) => {
                      if (!config.simulado && !config.has_blocks) {
                        updateConfig("type_time", e.target.value);
                      }
                    }}
                  >
                    <FormControlLabel disabled={config.simulado || config.has_blocks} control={<Radio checked={config.type_time == "all"} value="all" />} label={t("form_candidate_total_time")} />
                    <FormControlLabel disabled={config.simulado || config.has_blocks} control={<Radio checked={config.type_time == "restrict"} value="restrict" />} label={t("form_candidate_restricted_time")} />
                  </RadioGroup>
                  {config.type_time == "restrict" && !config.has_blocks && <Input
                    placeholder={t("ui_tempo_de_prova_em_minutos_que_o_candidato_tera_apo_f25d6411")}
                    value={config.tempo_prova}
                    disabled={config.simulado || config.has_blocks}
                    onChange={(e) => updateConfig("tempo_prova", e.target.value)}
                    required
                    min={1}
                    type="number"
                  />}
                </ConfigField>
              </InputGroup>
            </ConfigSection>}

            {faculdade?.empresa?.online_proccess !== false && config.online == true && <ConfigSection title={t("summary_proctoring")}>
              {!config.simulado && <InputGroup>
                <ConfigField label={t("label_proctoring_type")}>
                  <select
                    className="form-select"
                    value={config.fiscalizacao}
                    onChange={(e) => {
                      const fiscalizacao = e.target.value;
                      mergeConfig({
                        fiscalizacao,
                        chat_type: fiscalizacao == "image" ? config.chat_type : "all",
                        face_validation: false,
                        face_validation_periodicaly: false,
                        screen_sharing: false,
                        required_external_camera: false,
                        recording_start_before_enabled: false,
                        recording_start_before_minutes: 5,
                      });
                    }}
                  >
                    {config.fiscalizacoes.map((option) => (
                      <option key={option.id} value={option.id}>{translateOption(t, proctoringOptionKeys, option)}</option>
                    ))}
                  </select>
                </ConfigField>
              </InputGroup>}

              {config.simulado && <InputGroup>
                <ConfigField label={t("label_proctoring_type")}>
                  <select className="form-select" value={config.fiscalizacao} onChange={(e) => updateConfig("fiscalizacao", e.target.value)}>
                    {config.fiscalizacoes_simulado.map((option) => (
                      <option key={option.id} value={option.id}>{translateOption(t, simulationProctoringOptionKeys, option)}</option>
                    ))}
                  </select>
                </ConfigField>
              </InputGroup>}

              {config.fiscalizacao == "image" && <InputGroup>
                <ConfigField label={t("form_chat_type")}>
                  <select className="form-select" value={config.chat_type} onChange={(e) => updateConfig("chat_type", e.target.value)}>
                    {config.chat_types.map((option) => (
                      <option key={option.id} value={option.id}>{translateOption(t, chatOptionKeys, option)}</option>
                    ))}
                  </select>
                </ConfigField>
              </InputGroup>}

              {config.fiscalizacao != "none" && <InputGroup>
                <ConfigField label={t("ui_tempo_em_minuntos_anterior_a_avaliacao_que_deve_ap_6be44aeb")} description={t("ui_exemplo_caso_seja_colocado_60_minutos_entao_uma_ho_3f08b307")}>
                  <Input
                    placeholder={t("ui_tempo_em_minuntos_anterior_a_avaliacao_que_deve_ap_6be44aeb")}
                    value={config.chat_time}
                    min={1}
                    onChange={(e) => updateConfig("chat_time", e.target.value)}
                    required
                    type="number"
                  />
                </ConfigField>
              </InputGroup>}

              <InputGroup>
                <ConfigField label={t("form_block_multiple_extended_screens")}>
                  <BooleanRadio value={config.screen_extended_retrict} disabled={config.screen_sharing} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(screen_extended_retrict) => updateConfig("screen_extended_retrict", screen_extended_retrict)} />
                </ConfigField>
              </InputGroup>

              {config.fiscalizacao == "all" && config.mobile == false && <InputGroup>
                <ConfigField label={t(config.simulado ? "form_share_screen_simulation" : "form_record_candidate_screen")}>
                  <BooleanRadio value={config.screen_sharing} disabled={config.mobile} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(screen_sharing) => mergeConfig({ screen_sharing, ...(screen_sharing ? { screen_extended_retrict: true } : {}) })} />
                </ConfigField>
                <ConfigField label={t("form_face_microphone_entry_question")} description={t("ui_se_habilitada_o_candidato_so_conseguira_acessar_a__104a32e1")}>
                  <BooleanRadio value={config.face_validation} disabled={config.mobile} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(face_validation) => mergeConfig({ face_validation, ...(face_validation ? {} : { face_validation_periodicaly: false }) })} />
                </ConfigField>
                <ConfigField label={t("question_document_validation_on_entry")} description={t("description_document_validation_on_entry")}>
                  <BooleanRadio value={config.show_document} disabled={config.mobile} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(show_document) => updateConfig("show_document", show_document)} />
                </ConfigField>
                <ConfigField label={t("form_required_external_camera")} description={t("description_required_external_camera")}>
                  <BooleanRadio value={config.required_external_camera} disabled={config.mobile} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(required_external_camera) => updateConfig("required_external_camera", required_external_camera)} />
                </ConfigField>
                {!config.simulado && <ConfigField label={t("form_record_before_exam_question")} description={t("description_record_before_exam")}>
                  <BooleanRadio
                    value={config.recording_start_before_enabled}
                    yesLabel={t("answer_yes")}
                    noLabel={t("answer_no")}
                    onChange={(recording_start_before_enabled) => mergeConfig({
                      recording_start_before_enabled,
                      recording_start_before_minutes: recording_start_before_enabled ? (config.recording_start_before_minutes || 5) : 5,
                    })}
                  />
                </ConfigField>}
                {!config.simulado && config.recording_start_before_enabled == true && <ConfigField label={t("form_record_before_exam_minutes")}>
                  <Input
                    placeholder={t("form_record_before_exam_minutes")}
                    value={config.recording_start_before_minutes}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateConfig("recording_start_before_minutes", value === "" ? "" : Math.min(30, Math.max(1, parseInt(value, 10) || 1)));
                    }}
                    min={1}
                    max={30}
                    required
                    type="number"
                  />
                </ConfigField>}
              </InputGroup>}

              {config.fiscalizacao == "all" && config.mobile == false && config.face_validation == true && <InputGroup>
                <ConfigField label={t("ui_tempo_em_minuntos_anterior_a_avaliacao_que_deve_ap_018422a2")} description={t("ui_exemplo_caso_seja_colocado_60_minutos_entao_uma_ho_333a557f")}>
                  <Input
                    placeholder={t("ui_tempo_em_minuntos_anterior_a_avaliacao_que_deve_ap_018422a2")}
                    value={config.request_time}
                    onChange={(e) => updateConfig("request_time", e.target.value)}
                    required
                    min={1}
                    type="number"
                  />
                </ConfigField>
                <ConfigField label={t("form_face_microphone_periodic_question")} description={t("ui_se_habilitada_caso_apos_o_candidato_acessar_a_aval_76209b26")}>
                  <BooleanRadio value={config.face_validation_periodicaly} disabled={config.mobile} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(face_validation_periodicaly) => updateConfig("face_validation_periodicaly", face_validation_periodicaly)} />
                  {config.face_validation_periodicaly == true && <Input
                    placeholder={t("ui_tempo_sem_reconhecimento_para_o_candidato_ter_que__36fb327b")}
                    value={config.time_validation_periodicaly}
                    onChange={(e) => updateConfig("time_validation_periodicaly", e.target.value)}
                    required
                    min={20}
                    type="number"
                  />}
                </ConfigField>
              </InputGroup>}
            </ConfigSection>}

            {faculdade?.empresa?.online_proccess !== false && config.online == true && <ConfigSection title={t("summary_access")}>
              <InputGroup>
                <ConfigField label={t("ui_restringir_horario_de_acesso_do_candidato_ff671aed")} description={t("ui_candidatos_que_nao_tiverem_acessado_o_sistema_dent_db3aa123")}>
                  <BooleanRadio value={config.restrict_access} yesLabel={t("answer_yes")} noLabel={t("answer_no")} onChange={(restrict_access) => updateConfig("restrict_access", restrict_access)} />
                </ConfigField>
              </InputGroup>

              {config.restrict_access == true && <MuiPickersUtilsProvider utils={MomentUtils}>
                <InputGroup>
                  <ConfigField label={t("form_access_interval_start_date")}>
                    <DatePicker autoOk margin="normal" style={{ width: "100%" }} invalidDateMessage={t("ui_data_invalida_76fc5825")} ampm={false} cancelLabel={t("legacy_cancelar")} clearLabel={t("action_clear")} format="DD/MM/YYYY" value={config.inicio_access} onChange={(date) => updateConfig("inicio_access", date)} />
                  </ConfigField>
                  <ConfigField label={t("ui_horario_inicial_do_intervalo_dbb8ab8c")}>
                    <TimePicker autoOk margin="normal" style={{ width: "100%" }} invalidDateMessage={t("ui_horario_invalido_6d7a8390")} ampm={false} cancelLabel={t("legacy_cancelar")} clearLabel={t("action_clear")} value={config.hora_inicio_access} onChange={(date) => updateConfig("hora_inicio_access", date)} />
                  </ConfigField>
                </InputGroup>
                <InputGroup>
                  <ConfigField label={t("ui_data_de_encerramento_do_intervalo_ca148297")}>
                    <DatePicker autoOk margin="normal" style={{ width: "100%" }} invalidDateMessage={t("ui_data_invalida_76fc5825")} ampm={false} cancelLabel={t("legacy_cancelar")} clearLabel={t("action_clear")} format="DD/MM/YYYY" value={config.fim_access} onChange={(date) => updateConfig("fim_access", date)} />
                  </ConfigField>
                  <ConfigField label={t("legacy_horario_de_encerramento_do_intervalo")}>
                    <TimePicker autoOk margin="normal" style={{ width: "100%" }} invalidDateMessage={t("ui_horario_invalido_6d7a8390")} ampm={false} cancelLabel={t("legacy_cancelar")} clearLabel={t("action_clear")} value={config.hora_fim_access} onChange={(date) => updateConfig("hora_fim_access", date)} />
                  </ConfigField>
                </InputGroup>
              </MuiPickersUtilsProvider>}
            </ConfigSection>}

            <ConfigSection title={t("summary_candidate_resources")}>
              <InputGroup>
                <ConfigField label={t("ui_o_evento_sera_dividido_em_blocos_d2406ebe")} description={t("ui_caso_sim_e_possivel_definir_o_tempo_de_cada_bloco__e1f7d3b8")}>
                  <BooleanRadio
                    value={config.has_blocks}
                    disabled={config.simulado}
                    yesLabel={t("answer_yes")}
                    noLabel={t("answer_no")}
                    onChange={(has_blocks) => mergeConfig({ has_blocks, ...(has_blocks ? { type_time: "all", tempo_prova: "" } : {}) })}
                  />
                </ConfigField>
              </InputGroup>
            </ConfigSection>
          </Form>

          <ProcessoConfigSummary state={{ ...config, name: `${names.length} ${namesLabel.toLowerCase()}` }} user={{ empresa: faculdade?.empresa || { online_proccess: true } }} />
        </div>}

      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>{t("legacy_cancelar")}</Button>
        {activeSection == "config" && <Button variant="outline-secondary" onClick={() => setActiveSection("names")}>{t("action_back")}</Button>}
        {activeSection == "names" && <Button variant="primary" disabled={!hasPendingNames} onClick={goToConfigStep}>
          {t("action_next")}
        </Button>}
        {activeSection == "config" && <Button variant="success" disabled={loading || names.length === 0} onClick={saveBatch}>
          {loading && <Spinner animation="border" size="sm" className="me-2" />}
          {t("bulk_vacancy_save_button").replace(":entity", entity)}
        </Button>}
      </Modal.Footer>
    </Modal>
  );
};

export default ModalProcessosLote;
